<template>
    <div class="photos-panel">
      <div class="photos-panel-header">
        <div>
          <h6 class="mb-1">Photos du produit</h6>
          <small class="text-muted">
            {{ totalCount }} photo(s) enregistrée(s) · 10 Mo max par image
          </small>
        </div>

        <div class="photos-upload-actions">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :disabled="isWorking"
            @click="triggerMainPhotoInput"
          >
            <i class="fa fa-image"></i>
            <span class="btn-label ms-1">Ajouter la photo principale</span>
          </button>

          <button
            type="button"
            class="gallery-upload-button"
            :disabled="isWorking"
            @click="triggerGalleryPhotoInput"
          >
            <i class="fa fa-images"></i>
            <span>Sélectionner plusieurs photos</span>
          </button>

          <input
            ref="mainPhotoInput"
            type="file"
            class="visually-hidden"
            accept="image/*"
            @change="uploadMainPhoto"
          />

          <input
            ref="galleryPhotoInput"
            type="file"
            class="visually-hidden"
            accept="image/*"
            multiple
            @change="uploadGalleryPhotos"
          />
        </div>
      </div>
  
      <div class="photos-panel-body">
        <!-- Loader -->
        <div v-if="isLoading" class="text-center my-4">
          <i class="fa fa-spinner fa-spin me-2"></i>
          <span class="text-muted">Chargement des photos...</span>
        </div>
  
        <div v-else>
          <!-- Main -->
          <div class="photos-layout">
            <div class="main-photo-block">
              <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                <div class="small text-muted">
                  {{ mainPhotoUrl ? 'Photo principale' : 'Image par défaut' }}
                </div>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0"
                  :disabled="isWorking"
                  @click="triggerMainPhotoInput"
                >
                  <i class="fa fa-pen me-1" aria-hidden="true"></i>Remplacer
                </button>
              </div>
              <div class="photo-square large">
                <img :src="mainPhotoUrl || defaultProductImage" :alt="mainPhotoUrl ? 'Photo principale' : 'Image produit par défaut'" />
                <span v-if="mainPhotoUrl" class="badge bg-primary main-badge">
                  Principale
                </span>
              </div>
            </div>

            <div class="gallery-block">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div class="small text-muted">Galerie</div>
                  <small class="text-muted" v-if="photos.length">
                    {{ photos.length }} photo(s) supplémentaire(s)
                  </small>
                </div>
              </div>
  
              <div v-if="photos.length" class="photos-grid">
                <div v-for="p in photos" :key="p.id" class="photo-square">
                  <img :src="p.url" alt="Photo" />
  
                  <div class="photo-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light action-btn"
                      title="Définir comme principale"
                      @click="setAsMain(p)"
                      :disabled="isWorking"
                    >
                      <i class="fa fa-star"></i>
                    </button>
    
                    <AppLoadingButton
                      type="button"
                      class="btn btn-sm btn-danger action-btn"
                      title="Supprimer"
                      :loading="isDeletingPhoto(p)"
                      loading-label=""
                      @click="deletePhoto(p)"
                      :disabled="isWorking && !isDeletingPhoto(p)"
                    >
                      <i class="fa fa-trash"></i>
                    </AppLoadingButton>
                  </div>
                </div>
              </div>
    
              <div v-else class="gallery-empty-state" @click="triggerGalleryPhotoInput">
                <i class="fa fa-images"></i>
                <strong>Aucune photo dans la galerie</strong>
                <span>Sélectionnez plusieurs images pour enrichir la fiche produit.</span>
              </div>
            </div>
          </div>
  
          <!-- Messages -->
          <div v-if="errorMessage" class="alert alert-danger py-2 mt-3 mb-0">
            {{ errorMessage }}
          </div>
  
          <div v-if="successMessage" class="alert alert-success py-2 mt-3 mb-0">
            {{ successMessage }}
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { DEFAULT_PRODUCT_IMAGE } from '@/support/productImages';

  export default {
    name: 'ProductPhotosGallery',
    props: {
      productId: { type: [Number, String], required: true },
    },
    emits: ['changed'],
    data() {
      return {
        isLoading: false,
        isWorking: false,
        deletingPhotoId: null,
  
        mainPhotoUrl: '',
        photos: [],
        defaultProductImage: DEFAULT_PRODUCT_IMAGE,
  
        errorMessage: '',
        successMessage: '',
      };
    },
    computed: {
      totalCount() {
        return (this.mainPhotoUrl ? 1 : 0) + (this.photos ? this.photos.length : 0);
      },
    },
    mounted() {
      this.fetchPhotos();
    },
    methods: {
      async fetchPhotos() {
        if (!this.productId) return;
  
        this.isLoading = true;
        this.errorMessage = '';
        this.successMessage = '';
  
        try {
          const res = await this.$api.get(`/products/${this.productId}/photos`);
          const data = res.data.data || res.data || {};
  
          this.mainPhotoUrl = data.main_photo_url || (data.main_photo ? data.main_photo.url : '');
          this.photos = data.photos || [];
        } catch (e) {
          console.error('Erreur chargement photos produit', e);
          this.errorMessage = "Impossible de charger les photos.";
          this.mainPhotoUrl = '';
          this.photos = [];
        } finally {
          this.isLoading = false;
        }
      },

      triggerMainPhotoInput() {
        this.$refs.mainPhotoInput?.click();
      },

      triggerGalleryPhotoInput() {
        this.$refs.galleryPhotoInput?.click();
      },

      async uploadMainPhoto(event) {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        event.target.value = '';

        if (!file) return;

        await this.uploadPhotos({ mainPhoto: file });
      },

      async uploadGalleryPhotos(event) {
        const files = event.target.files ? Array.from(event.target.files) : [];
        event.target.value = '';

        if (!files.length) return;

        await this.uploadPhotos({ photos: files });
      },

      async uploadPhotos({ mainPhoto = null, photos = [] } = {}) {
        if (!this.productId || (!mainPhoto && !photos.length)) return;

        this.isWorking = true;
        this.errorMessage = '';
        this.successMessage = '';

        try {
          const formData = new FormData();

          if (mainPhoto) {
            formData.append('main_photo', mainPhoto);
          }

          photos.forEach((photo) => {
            formData.append('photos[]', photo);
          });

          await this.$api.post(`/products/${this.productId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          this.successMessage = mainPhoto
            ? 'Photo principale enregistrée.'
            : 'Photos ajoutées à la galerie.';

          await this.fetchPhotos();
          this.$emit('changed');
        } catch (e) {
          console.error('Erreur upload photos produit', e);
          this.errorMessage = e.response?.data?.errors
            ? Object.values(e.response.data.errors).flat().join(' ')
            : "Une erreur est survenue lors de l'enregistrement des photos.";
        } finally {
          this.isWorking = false;
        }
      },
  
      async setAsMain(photo) {
        if (!photo || !photo.id) return;
  
        const ok = await this.$confirmAction("Définir cette photo comme photo principale ?");
        if (!ok) return;
  
        this.isWorking = true;
        this.errorMessage = '';
        this.successMessage = '';
  
        try {
          await this.$api.post(`/product-photos/${photo.id}/set-main`);
  
          this.successMessage = "Photo principale mise à jour.";
          await this.fetchPhotos();
          this.$emit('changed');
        } catch (e) {
          console.error('Erreur set main photo', e);
          const msg =
            e.response?.data?.errors
              ? Object.values(e.response.data.errors).flat().join(' ')
              : "Une erreur est survenue lors du changement de photo principale.";
          this.errorMessage = msg;
        } finally {
          this.isWorking = false;
        }
      },
  
      async deletePhoto(photo) {
        if (!photo || !photo.id) return;
        if (this.isDeletingPhoto(photo)) return;

        this.deletingPhotoId = photo.id;
  
        const ok = await this.$confirmAction("Supprimer cette photo définitivement ?");
        if (!ok) {
          this.deletingPhotoId = null;
          return;
        }
  
        this.isWorking = true;
        this.errorMessage = '';
        this.successMessage = '';
  
        try {
          await this.$api.delete(`/product-photos/${photo.id}`);
  
          this.successMessage = "Photo supprimée.";
          await this.fetchPhotos();
          this.$emit('changed');
        } catch (e) {
          console.error('Erreur suppression photo', e);
          const msg =
            e.response?.data?.errors
              ? Object.values(e.response.data.errors).flat().join(' ')
              : "Une erreur est survenue lors de la suppression de la photo.";
          this.errorMessage = msg;
        } finally {
          this.isWorking = false;
          this.deletingPhotoId = null;
        }
      },

      isDeletingPhoto(photo) {
        return Number(this.deletingPhotoId || 0) === Number(photo?.id || 0);
      },
    },
  };
  </script>
  
  <style scoped>
  .photos-panel {
    background: #ffffff;
    border: 1px solid #e5ebf4;
    border-radius: 8px;
  }

  .photos-panel-header {
    align-items: center;
    border-bottom: 1px solid #e5ebf4;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    padding: 0.9rem 1rem;
  }

  .photos-upload-actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    justify-content: flex-end;
  }

  .gallery-upload-button {
    align-items: center;
    background: linear-gradient(135deg, #0f766e, #14b8a6);
    border: 0;
    border-radius: 8px;
    color: #ffffff;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 800;
    gap: 0.45rem;
    min-height: 34px;
    padding: 0.45rem 0.85rem;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }

  .gallery-upload-button:hover,
  .gallery-upload-button:focus {
    box-shadow: 0 10px 24px rgba(20, 184, 166, 0.24);
    transform: translateY(-1px);
  }

  .gallery-upload-button:disabled {
    cursor: wait;
    opacity: 0.68;
    transform: none;
  }

  .photos-panel-body {
    padding: 1rem;
  }

  .photos-layout {
    align-items: start;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(180px, 260px) minmax(0, 1fr);
  }

  .main-photo-block,
  .gallery-block {
    min-width: 0;
  }

  .photos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
  }
  
  @media (max-width: 992px) {
    .photos-layout {
      grid-template-columns: 1fr;
    }

    .photos-panel-header {
      align-items: flex-start;
      flex-direction: column;
    }
  }
  
  @media (max-width: 576px) {
    .photos-upload-actions {
      align-items: stretch;
      flex-direction: column;
      width: 100%;
    }

    .photos-upload-actions .btn,
    .gallery-upload-button {
      justify-content: center;
      width: 100%;
    }
  }
  
  .photo-square {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 0.75rem;
    overflow: hidden;
    background: #f3f4f6;
    border: 1px solid rgba(0,0,0,0.06);
  }
  
  .photo-square.large {
    max-width: none;
  }
  
  .photo-square img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .photo-actions {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: flex;
    gap: 6px;
  }
  
  .action-btn {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .main-badge {
    position: absolute;
    left: 8px;
    top: 8px;
  }

  .gallery-empty-state {
    align-items: center;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #64748b;
    cursor: pointer;
    display: grid;
    gap: 0.35rem;
    justify-items: center;
    min-height: 180px;
    padding: 1.5rem;
    text-align: center;
    transition: border-color 0.18s ease, color 0.18s ease;
  }

  .gallery-empty-state:hover {
    border-color: #14b8a6;
    color: #0f766e;
  }

  .gallery-empty-state i {
    font-size: 1.55rem;
  }
  </style>
  
